import { queryOptions as dbQueryOptions } from '@w/react-query/db'
import { trainingFlashcardOptions, trainingPageOptions } from '../../router/workspace-queries'
import { returnToTrainingEditor } from '../../training/return-to-pages'
import { DeletedReviewEditor, deletedReviewBlocks } from '../../editor/deleted-review'
import { readPromptType } from '../../db/question-card'
import { questionSourceValid } from '../../db/question-source'
import { actOnCard, cloneDeletedCard, acceptCard } from '../../db/learner-actions'
import { getFlashcardsMediaFilePath } from '../../server/media/flashcards-media-be'
import { useUserSpaces } from '../../router/authenticated-layout'
import type { Tel } from '@w/tel'
import { applyTranslation, type TranslationRow } from '@w/content-blocks'
import { nativeTongueFor } from '../../translation/native-language'
import { useSpacesView } from '../../db/spaces-queries'
import { unknownErrorToError, ulid, safe } from '@w/utils'
import { answerViewerState, gradeQuestion, graders as _graders, transcribeAnswer } from '../../training/grade-question'
import { trainingControls, type TrainingLevel } from '../../training/training-controls'
import { ErrorDetails } from '@w/ui/components/error-details'
import { ImageLoadingProvider } from '@w/ui/components/image'
import { storedMediaPaths as getStoredMediaPaths } from '../../media/media-paths'
import { usePreparedMedia } from '../../media/prepared-media'
import { withoutActWarning, useKeyboardShortcut, useViewportInset, useScrollContainer, type ShortcutEvent } from '@w/utils-fe'
import { useMutation, useQuery, useSuspenseQuery } from '@w/react-query'
import { AudioOptionsProvider, AudioRecorder } from '@w/react-audio'
import {
  type Block,
  type Blocks,
} from '@w/content-blocks/src/block'
import { UFile } from '@w/utils'
import { Button } from '@w/ui/components/button'
import { Badge } from '@w/ui/components/badge'
import { IconButton } from '@w/ui/components/icon-button'
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  PencilIcon,
  SendIcon,
  SkipForwardIcon,
  XIcon,
} from '@w/ui/components/icon'
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from '@w/ui/components/menu'
import {
  Progress,
  ProgressIndicator,
  ProgressTrack,
} from '@w/ui/components/progress'
import { Toolbar, ToolbarButton, ToolbarGroup } from '@w/ui/components/toolbar'
import {
  BlockEditor,
  useEditorState,
  type BlockEditorHandle,
  type QuestionAnswerValue,
} from '@w/content-blocks-editor'
import { pageDocument } from '@w/content-blocks-editor/doc-bindings-for-page'
import { Doc } from '@w/crdt'
import {
  type ReactElement,
  startTransition,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from '../../i18n'
import {
  useLoaderData,
  useLocationState,
  useNavigate,
  useParams,
  useQueue,
  useRouterContext,
  useSearch,
} from '../../router/flashcards-fe.router-bindings'
import {
  canSubmitTrainingBlock,
  getTrainingExcerptBlocks,
  getTrainingParts,
  describeTrainingAnswer,
  revealTrainingParts,
  trainingChoices,
  shuffleTrainingChoiceOptions,
} from '../../training/training-scoring'
import { listAdoptedRuleMembers } from '../../training/training-rule-members'
import { writeTrainingReview } from '../../training/write-review'
import { writeWorkspace } from '../../db/workspace-write'
import { LoadingSpinner } from '../../shell/loading-spinner'
import { getLexiconCardType } from '../../lexicon/lexicon-cards'
import type { LexiconEntry } from '../../lexicon/lexicon-model'
import type { Clock } from '../../router/flashcards-fe.router-ctx'
import type { SpaceResource } from '../../router/spaces-resources'
import { pageBlocks, projectedPageBlocks } from '../page-blocks'
import {
  DifficultyLevelIcon,
  ImportanceLevelIcon,
} from './training-level-icons'

/**
 * The sitting IS the navigation queue (QUEUE.md). Session counters are queue
 * state — going back must not un-answer a card. Per-card presentation is entry
 * state — going back must restore the card as you left it. Neither lives in an
 * app-level store any more, and neither is carried in the URL.
 */
type TrainingCounters = { answered: number; errors: number; total: number }
// partMarks: per-part result of a multi-part question, shown on reveal like mc rows.
type TrainingEntryState = { resolution: string; mark: number; partMarks?: number[] }

// flashcardEvents are read back ordered by id, so review/graduated event ids
// must sort by creation order even when multiple events land within the same
// millisecond (plain ulid()'s random suffix would sort them arbitrarily).

export function TrainingEmptyPage() {
  const ctx = useRouterContext()
  const navigate = useNavigate()
  const loaderData = useLoaderData({ from: '/$workspaceId/training' })
  const params = useParams({ from: '/$workspaceId/training' })
  const search = useSearch({ from: '/$workspaceId/training' })
  const { data: flashcards, isLoading } = useQuery(
    loaderData.flashcardsOptions, loaderData.queryClient,
  )
  const recoveryExhausted = search.recovery === '2'
  const startedRef = useRef(false)

  // Birth is the loader's redirect (§4.2). When the query resolves after the
  // loader already ran with a cold cache, re-enter the route under a new search
  // so the loader re-runs — now with the cards in hand — and births the queue.
  useEffect(() => {
    const cards = flashcards ?? []
    if (recoveryExhausted || cards.length === 0 || startedRef.current) return
    startedRef.current = true
    navigate({
      to: '/$workspaceId/training',
      params: { workspaceId: params.workspaceId },
      search: {
        total: String(cards.length),
        recovery: search.recovery,
      },
      replace: true,
    })
  }, [
    flashcards,
    navigate,
    params.workspaceId,
    recoveryExhausted,
    search.recovery,
  ])

  if (
    !recoveryExhausted &&
    (isLoading || (flashcards?.length ?? 0) > 0)
  ) {
    return <LoadingSpinner />
  }

  return (
    <main
      data-testid="flashcards-training-page"
      className="bg-surface-1 text-on-surface h-dvh max-h-full overflow-hidden"
    >
      <div className="mx-auto flex h-full min-h-0 max-w-2xl flex-col">
        <TrainingEmptyState
          onBack={() => {
            void returnToTrainingEditor(ctx, params.workspaceId, navigate)
          }}
        />
      </div>
    </main>
  )
}

export function TrainingEndPage() {
  const ctx = useRouterContext()
  const params = useParams({ from: '/$workspaceId/training/end' })
  const navigate = useNavigate()
  const search = useSearch({ from: '/$workspaceId/training/end' })
  const { t } = useTranslation('training')
  const errors = Number(search.errors ?? 0)
  const total = Number(search.total ?? 0)
  const correctPercentage =
    total > 0 ? Math.round(((total - errors) / total) * 100) : 0

  return (
    <main
      data-testid="flashcards-training-page"
      className="bg-surface-1 text-on-surface h-dvh max-h-full overflow-hidden"
    >
      <div className="mx-auto flex h-full min-h-0 max-w-2xl flex-col">
        <section
          data-testid="flashcards-training-stats"
          className="flex flex-1 items-center justify-center px-6 text-center sm:px-8"
        >
          <div className="flex w-full flex-col items-center gap-8">
            <h1 className="text-3xl font-bold">{t('doneTitle')}</h1>
            <div className="flex flex-col items-center gap-1">
              <p className="text-6xl font-semibold tracking-tight tabular-nums">
                {correctPercentage}%
              </p>
              <p className="text-on-surface-secondary text-sm">
                {errors === 0
                  ? t('statsNoFailedCards')
                  : `${t('statsFailedCards')}: ${errors}`}
              </p>
            </div>
            <div className="w-full px-6 sm:px-16">
              <Button
                className="w-full"
                data-testid="flashcards-training-summary-back"
                variant="filled"
                onClick={() => {
                  void returnToTrainingEditor(ctx, params.workspaceId, navigate)
                }}
              >
                <ChevronLeftIcon />
                {t('backToPages')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </main>
  )
}

function TrainingEmptyState({ onBack }: { onBack: () => void }) {
  const { t } = useTranslation('training')
  return (
    <section
      className="flex flex-1 flex-col items-center justify-center gap-8 px-6 text-center sm:px-8"
      data-testid="flashcards-training-empty"
    >
      <h1 className="text-balance text-3xl font-bold">{t('emptyTitle')}</h1>
      <div className="w-full px-6 sm:px-16">
        <Button
          className="w-full"
          data-testid="flashcards-training-empty-back"
          variant="filled"
          onClick={onBack}
        >
          <ChevronLeftIcon />
          {t('backToPages')}
        </Button>
      </div>
    </section>
  )
}

export function TrainingPage(): ReactElement | null {
  const ctx = useRouterContext()
  const params = useParams({ from: '/$workspaceId/training/$flashcardId' })
  const loaderData = useLoaderData({ from: '/$workspaceId/training/$flashcardId' })
  const spaces = useUserSpaces()
  const { data, error } = useSpacesView(spaces, loaderData.workspaceId, ctx.queryClient)
  if (error) throw error
  if (!data) return null
  // Render reads only the published view: the live registry (spaces.get/db) runs ahead of it while an access reset replaces the replica.
  const content = data.resources.find(resource => resource.space.id === loaderData.contentSpaceId)
  const progress = data.resources.find(resource => resource.space.id === loaderData.progressWorkspaceId)
    ?? data.resources.find(resource => resource.space.kind === 'private' && resource.space.ownerUserId === spaces.userId) ?? data.resources[0]
  if (!content || !progress || progress.space.workspaceId !== loaderData.progressWorkspaceId) return <p role="status">Question unavailable</p>
  return <TrainingContent key={`${spaces.userId}:${content.connectionId}:${params.flashcardId}`} resources={{ progress, content }} />
}

type TrainingResources = { progress: SpaceResource; content: SpaceResource }

function TrainingContent({ resources }: { resources: TrainingResources }) {
  const params = useParams({ from: '/$workspaceId/training/$flashcardId' })
  const routerCtx = useRouterContext()
  const resource = resources.progress
  const { data: cardRows } = useSuspenseQuery(trainingFlashcardOptions(routerCtx, params), resource.queryClient)
  const [sittingCard, setSittingCard] = useState(cardRows[0])
  if (sittingCard?.qid !== params.flashcardId && cardRows[0]?.qid === params.flashcardId) setSittingCard(cardRows[0])
  const contentResource = resources.content
  const sourcePageId = cardRows[0]?.pageId ?? (sittingCard?.qid === params.flashcardId ? sittingCard.pageId : '')
  const { data: pages } = useSuspenseQuery(trainingPageOptions(routerCtx, {workspaceId: contentResource.space.id, pageId: sourcePageId, qid: cardRows[0]?.deletedBy && cardRows[0].touchedAt !== null ? params.flashcardId : undefined}), contentResource.queryClient)
  const currentCard = cardRows[0]
  const currentSource = currentCard && pages.some(page => page.id === currentCard.pageId)
  const flashcard = currentSource ? currentCard : sittingCard?.qid === params.flashcardId ? sittingCard : currentCard
  if (!flashcard) throw new Error('Flashcard not found')
  const page = pages.find(candidate => candidate.id === flashcard.pageId)
  if (!page) return <p role="status">Question unavailable</p>
  return <TrainingResolvedContent flashcard={flashcard} sittingCard={sittingCard} page={page} resources={resources} />
}

type TrainingSourceCard = Awaited<ReturnType<ReturnType<typeof trainingFlashcardOptions>['queryFn']>>[number]
type TrainingSourcePage = Awaited<ReturnType<ReturnType<typeof trainingPageOptions>['queryFn']>>[number]

function TrainingResolvedContent({flashcard, sittingCard, page, resources}: {flashcard: TrainingSourceCard; sittingCard: TrainingSourceCard | undefined; page: TrainingSourcePage; resources: TrainingResources}) {
  const { inset: keyboardInset, keyboardOpen } = useViewportInset()
  const navigate = useNavigate()
  const loaderData = useLoaderData({
    from: '/$workspaceId/training/$flashcardId',
  })
  const params = useParams({ from: '/$workspaceId/training/$flashcardId' })
  const routerCtx = useRouterContext()
  const { auth } = routerCtx
  const db = resources.progress.db
  const [user] = useState(() => auth.getUser())
  const translationLanguage =
    nativeTongueFor(user.settings.lexicon?.nativeLanguage, routerCtx.defaultNativeLanguage)
  const { t } = useTranslation('training')
  // queue state = the sitting's counters; entry state = this card's resolution
  const {
    id: sittingId,
    size,
    state: sittingCounters,
    navigate: advanceQueue,
  } = useQueue<
    { workspaceId: string; flashcardId: string },
    TrainingCounters | undefined
  >()
  // Route-keyed persisted queues can belong to a different workspace. A direct
  // card there starts its own one-card session, even when the old queue drained.
  const counters = sittingId === params.workspaceId || sittingId === `${params.workspaceId}:recovery`
    ? sittingCounters : undefined
  const [entry, setEntry] = useLocationState<TrainingEntryState>()
  const sessionTotal = counters?.total ?? 1
  const answered = counters?.answered ?? 0
  const progressCurrent = Math.min(answered + 1, sessionTotal)
  const [feedback, setFeedback] = useState<string | null>(null)
  // Scoring/review error surfaced to the user. NEVER swallow a thrown error.
  const [gradeError, setGradeError] = useState<unknown>(null)
  const [levels, setLevels] = useState<{
    importance: TrainingLevel
    difficulty: TrainingLevel
  }>({ importance: 'medium', difficulty: 'medium' })
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const editorHandleRef = useRef<BlockEditorHandle | null>(null)
  const [handle, setHandle] = useState<BlockEditorHandle | null>(null)
  // The flashcard id the editor was last cleared for. onReady
  // can fire again after host renders. Clearing then would wipe the user's typed
  // answer, so only clear when the card itself changes.
  const clearedForCardRef = useRef<string | null>(null)
  const startedAtRef = useRef(0)
  const timeoutInFlightRef = useRef(false)
  const submitInFlightRef = useRef(false)
  const submissionRef = useRef<{ qid: string } | null>(null)
  const [trainingBlocksCache] = useState(_createTrainingBlocksCache)
  const [trainingDocCache] = useState(_createTrainingDocCache)
  const item = {
    flashcard,
    question: { qid: flashcard.qid },
    page,
  }
  const answers = useEditorState(s => s.answers, handle)
  const content = item.page.content
  const pendingDeletion = flashcard.touchedAt !== null && flashcard.deletedBy !== null && flashcard.deletedBy !== user.id
  const deletedReview = pendingDeletion && entry.resolution === 'deleted-review'
  const removedQuestion = pendingDeletion && page.removedParts.length > 0 ? page.removedParts : undefined
  const effectiveContent = removedQuestion ? { kind: 'note' as const, children: removedQuestion } : content
  // D4: the class card's practice is projected per rep from real review
  // state, never authored. `pageBlocks` stays sync/content-only for other
  // call sites (page viewer, cards admin); this is the actual study render
  // path, so it needs `projectedPageBlocks` instead — but only an editable
  // page carrying a `rule` block needs the async member/rep lookup. Every
  // other page (the overwhelming majority of cards) renders synchronously,
  // with no query and no loading flash.
  const hasRuleBlock =
    'kind' in effectiveContent &&
    (effectiveContent.kind === 'note' || effectiveContent.kind === 'language') &&
    effectiveContent.children.some((block) => block.type === 'rule' || block.type === 'questionGroup')
  // The card's own review count picks a rule's or question group's practice item (flashcards.reviewCount), never the event log.
  const reviewCount = flashcard.reviewCount
  // The page row came from the content resource's query, so its space is that resource.
  const sourceDb = resources.content.db
  // Invalidation is table-level: this source-page dependency also revalidates
  // selection when any member entry changes. The callback only reads.
  const sourceOptions = dbQueryOptions(sourceDb.query`SELECT ${sourceDb.pages.id} FROM ${sourceDb.pages}
    WHERE ${sourceDb.pages.id.eq(item.page.id)}`, { id: sourceDb.pages.id })
  const { data: projectedPractice } = useQuery({
    ...sourceOptions,
    queryKey: [...sourceOptions.queryKey, 'rule-practice', flashcard.qid, reviewCount, effectiveContent, translationLanguage],
    enabled: hasRuleBlock,
    queryFn: async () => {
      const rep = reviewCount
      const blocks = await projectedPageBlocks(
        effectiveContent,
        {
          members: (language, inflectionClass, repetition) =>
            listAdoptedRuleMembers(sourceDb, {
              workspaceId: params.workspaceId, language, inflectionClass, rep: repetition,
            }),
          rep: qid => Promise.resolve(qid === flashcard.qid ? rep : 0),
        },
        translationLanguage,
        flashcard.qid,
      )
      return { blocks, rep }
    },
  }, resources.content.queryClient)
  const syncBlocks = removedQuestion ?? pageBlocks(content, translationLanguage)
  const blocks = _getPageBlocks(
    hasRuleBlock ? (projectedPractice?.blocks ?? syncBlocks) : syncBlocks,
  )
  const trainingBlocks = trainingBlocksCache.get({
    blocks,
    flashcardId: flashcard.qid,
    random: routerCtx.testSupport?.trainingOptionRandom,
  })
  const excerptBlocks = getTrainingExcerptBlocks(
    trainingBlocks,
    item.question.qid,
  )
  const lexiconTaskLabel =
    'kind' in content && content.kind === 'lexiconEntry'
      ? _lexiconTaskLabel(content.entry, item.question.qid)
      : undefined
  const translations = page?.translationRows ?? []
  const displayedExcerptBlocks = applyTranslation(lexiconTaskLabel
    ? _withoutLexiconTaskLabel(excerptBlocks, item.question.qid, lexiconTaskLabel)
    : excerptBlocks, _choiceTranslationRows(translations, blocks, trainingBlocks))
  const score = entry.resolution === 'marked' ? entry.mark : null
  const revealed = entry.resolution === 'revealed'
  const showAnswer = entry.resolution !== 'unanswered'
  // The card the learner studies is a render of view state: an excerpt of the page,
  // with shuffled choices and — once revealed — the answer marked. It is never
  // persisted, so it lives in its own throwaway document, rebuilt only when what the
  // learner should see changes. The page's own document is untouched by training.
  const trainingDoc = trainingDocCache.get(
    showAnswer || flashcard.deletedAt !== null
      ? revealTrainingParts(displayedExcerptBlocks, item.question.qid, score, entry.partMarks)
      : displayedExcerptBlocks,
  )
  // One qid = one question; each block carrying it is a part with its own answer.
  // Evaluation is mc-like: the card is right only when every part is right.
  // INV-parts: parts are read from the doc the learner sees (translated, shuffled),
  // under the doc's own block ids — the ids the editor keys answers by. Grading,
  // answers and reveal marks all index this one list.
  const parts = getTrainingParts(pageDocument(trainingDoc).content.children as unknown as Block[], item.question.qid)
  const promptBlock = parts[0] ?? null
  const sourceValid = questionSourceValid(parts) && promptBlock !== null
  const authoredPromptType = promptBlock ? readPromptType(promptBlock) : null
  const incompatiblePrompt = sourceValid && authoredPromptType !== flashcard.promptType
  const validQuestion = flashcard.deletedAt !== null || (sourceValid && !incompatiblePrompt)
  const answer = (promptBlock && answers[promptBlock.id]) ?? {}
  const visibleBlocks = pageDocument(trainingDoc).content.children as unknown as Blocks
  const mediaPaths = getStoredMediaPaths(deletedReview || flashcard.deletedAt !== null ? deletedReviewBlocks(visibleBlocks) : excerptBlocks)
  const media = usePreparedMedia(mediaPaths)
  // Unavailable media renders the shared per-image fallback; it never blocks the card.
  const mediaPrepared = !media.pending
  const cardBlocked = !validQuestion || deletedReview || !mediaPrepared
  const trainingFileHandler = { ..._noopFileHandler, getUrl: media.resolve }
  const aiKind = promptBlock?.type === 'cloze' ? 'cloze' :
    promptBlock?.type === 'question' && promptBlock.subtype === 'la' ? 'la' : null
  const aiControls = aiKind ? trainingControls(aiKind, levels.difficulty, levels.importance) : null
  // One voice transcript cannot fill several parts.
  const controls = aiControls && parts.length > 1 ? { ...aiControls, voice: false } : aiControls
  const spokenResponse =
    promptBlock?.type === 'question' && promptBlock.subtype === 'sr'
  const singleChoice =
    parts.length === 1 && promptBlock?.type === 'question' && promptBlock.subtype === 'sc'
  const ttaSeconds = Math.max(sittingCard?.ttaSeconds ?? item.flashcard.ttaSeconds, 1)
  const timeoutSeconds = ttaSeconds * 2
  const remainingSeconds = Math.max(timeoutSeconds - elapsedSeconds, 0)
  const timerTone =
    elapsedSeconds >= ttaSeconds * 1.5
      ? 'text-error'
      : elapsedSeconds >= ttaSeconds
        ? 'text-warning'
        : 'text-info'
  const hasAnswer = parts.some(part => _hasPartAnswer(answers[part.id] ?? {}))
  const canReveal =
    controls ? controls.reveal : hasAnswer

  const [grading, setGrading] = useState(false)
  useEffect(() => {
    const cardId = item.flashcard.qid
    return () => {
      if (submissionRef.current?.qid === cardId) submissionRef.current = null
    }
  }, [item.flashcard.qid])

  const reviewMutation = useMutation({
    async mutationFn(input: {
      flashcardId: string
      mark: number
      ttaSeconds: number
      tel: Tel
    }) {
      await writeTrainingReview(routerCtx, loaderData.progressWorkspaceId, { ...input, practiceRep: projectedPractice?.rep })
      return { pending: false }
    },
  })
  const promptRepair = useMutation({
    mutationFn: () => {
      if (!authoredPromptType) throw new Error('Question source unavailable')
      return writeWorkspace(routerCtx, loaderData.progressWorkspaceId, () => actOnCard(db, {
        userId: user.id, deviceId: routerCtx.deviceId, qid: flashcard.qid,
        action: { kind: 'rated', patch: { promptType: authoredPromptType } },
      }))
    },
  })
  const levelMutation = useMutation({
    mutationFn(input: {
      field: 'importance' | 'difficulty'
      value: TrainingLevel
      previous: TrainingLevel
    }) {
      return writeWorkspace(routerCtx, loaderData.progressWorkspaceId, () => actOnCard(db, {
        userId: user.id, deviceId: routerCtx.deviceId, qid: item.flashcard.qid,
        action: { kind: 'rated', patch: { [input.field]: input.value } },
      }))
    },
    onError(_error, input) {
      setLevels((current) => ({ ...current, [input.field]: input.previous }))
    },
  })
  const acceptDeletionMutation = useMutation({
    mutationFn(input: { flashcardId: string }) {
      return writeWorkspace(routerCtx, loaderData.progressWorkspaceId, () => acceptCard(db, {
        userId: user.id, deviceId: routerCtx.deviceId, qid: input.flashcardId,
      }))
    },
  })
  const [cloneOperations] = useState(() => new Map<string, { key: string; pageId: string; eventId: string }>())
  function cloneOperationForCurrentCard() {
    const pageRemoval = item.page.deletedAt
    const key = `flashcards:clone:${user.id}:${loaderData.progressWorkspaceId}:${params.flashcardId}:${item.page.id}:${pageRemoval ?? item.page.updatedAt.getTime()}`
    const existing = cloneOperations.get(key)
    if (existing) return existing
    const retained = routerCtx.localStorage.getItem(key)
    const operation = retained ? JSON.parse(retained) as { pageId: string; eventId: string } : { pageId: ulid(), eventId: ulid() }
    const result = { key, ...operation }
    cloneOperations.set(key, result)
    return result
  }
  const cloneMutation = useMutation({ mutationFn: async () => {
    const cloneOperation = cloneOperationForCurrentCard()
    const privateSpace = safe(routerCtx.spaces.snapshot(loaderData.progressWorkspaceId).resources.find(resource => resource.space.kind === 'private' && resource.space.ownerUserId === user.id), 'Private space unavailable')
    routerCtx.localStorage.setItem(cloneOperation.key, JSON.stringify({ pageId: cloneOperation.pageId, eventId: cloneOperation.eventId }))
    await writeWorkspace(routerCtx, loaderData.progressWorkspaceId, () => cloneDeletedCard(db, {
      userId: user.id, deviceId: routerCtx.deviceId, qid: flashcard.qid, privateSpaceId: privateSpace.space.id, personalRootId: privateSpace.space.personalRootId,
      pageId: cloneOperation.pageId, eventId: cloneOperation.eventId,
      media: {
        destination: (_source, index) => getFlashcardsMediaFilePath({ workspaceId: loaderData.progressWorkspaceId, pageId: privateSpace.space.personalRootId }, `clone-${cloneOperation.eventId}-${index}`),
        async copy({ source, destination }) {
          const file = safe(await routerCtx.bucket.get(source), `Missing media: ${source}`)
          await routerCtx.bucket.put(new UFile(file.blob, { path: destination }))
        },
      },
    }))
    navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: { workspaceId: loaderData.progressWorkspaceId, spaceId: privateSpace.space.id, pageId: cloneOperation.pageId } })
  } })
  const directory = useQuery(routerCtx.rpc.workspace.list.queryOptions(), routerCtx.queryClient)
  const actorName = directory.data?.memberships.find(member => member.workspaceId === loaderData.progressWorkspaceId && member.userId === flashcard.deletedBy)?.email ?? flashcard.deletedBy
  const resetCardState = useEffectEvent((cardId: string) => {
    if (cardId !== item.flashcard.qid) return
    startTransition(() => {
      setFeedback(null)
      setGradeError(null)
      setGrading(false)
      setLevels({
        importance: item.flashcard.importance,
        difficulty: item.flashcard.difficulty,
      })
      setElapsedSeconds(0)
    })
    editorHandleRef.current?.clearAnswers()
    timeoutInFlightRef.current = false
    submissionRef.current = null
    submitInFlightRef.current = false
    startedAtRef.current = routerCtx.clock.now()
  })

  useEffect(() => {
    if (!mediaPrepared) return
    resetCardState(item.flashcard.qid)
  }, [mediaPrepared, item.flashcard.qid])

  const timeoutReview = useEffectEvent(async () => {
    if (cardBlocked || timeoutInFlightRef.current || reviewMutation.isPending || submitInFlightRef.current) return
    timeoutInFlightRef.current = true
    if (gradeError !== null) setGradeError(null)

    try {
      await routerCtx.tel.span({ name: 'training.timeout', target: 'job', input: { qid: item.flashcard.qid } }, tel => reviewMutation.mutateAsync({
        tel,
        flashcardId: item.flashcard.qid,
        mark: 0,
        ttaSeconds: timeoutSeconds,
      }))
      startTransition(() => {
        setEntry({ resolution: 'marked', mark: 0 })
      })
    } catch (error) {
      timeoutInFlightRef.current = false
      startTransition(() => {
        setGradeError(error)
      })
    }
  })

  useEffect(() => {
    const cancel =
      !cardBlocked && mediaPrepared && item.flashcard.deletedAt === null && item.flashcard.qid.length > 0 && score === null
        ? routerCtx.clock.every(() => {
            if (submitInFlightRef.current) return
            const nextElapsedSeconds = _elapsedSeconds(routerCtx.clock, startedAtRef.current)
            withoutActWarning(() => { setElapsedSeconds(nextElapsedSeconds) })
            if (nextElapsedSeconds >= timeoutSeconds) {
              queueMicrotask(() => {
                void timeoutReview()
              })
            }
          }, 1_000)
        : null
    return () => {
      cancel?.()
    }
  }, [cardBlocked, mediaPrepared, item.flashcard.qid, item.flashcard.deletedAt, score, timeoutSeconds, routerCtx.clock])

  function handleLevelChange(
    field: 'importance' | 'difficulty',
    value: string | null,
  ) {
    if (cardBlocked || (value !== 'low' && value !== 'medium' && value !== 'high')) return
    const previous = levels[field]
    setLevels((current) => ({ ...current, [field]: value }))
    levelMutation.mutate({ field, value, previous })
  }

  function handleEditorReady(handle: BlockEditorHandle) {
    editorHandleRef.current = handle
    if (clearedForCardRef.current !== item.flashcard.qid) {
      handle.clearAnswers()
      clearedForCardRef.current = item.flashcard.qid
    }
    setHandle(handle)
  }

  function handleBack() {
    // Navigation may await page reads before unmount; late grading is already canceled.
    submissionRef.current = null
    void returnToTrainingEditor(routerCtx, params.workspaceId, navigate)
  }

  // viewerState overrides the first part's answer (a just-clicked choice, a voice take).
  async function handleSubmit(viewerState = answerViewerState(answer), recording?: UFile) {
    if (cardBlocked || !promptBlock || spokenResponse || revealed || score !== null || submitInFlightRef.current || reviewMutation.isPending) return
    const partStates = parts.map((part, index) => index === 0 ? viewerState : answerViewerState(answers[part.id] ?? {}))
    if (!recording && !parts.some((part, index) => canSubmitTrainingBlock(part, partStates[index]))) return
    submitInFlightRef.current = true
    const submission = { qid: item.flashcard.qid }
    submissionRef.current = submission
    setGrading(true)
    const ttaSeconds = _elapsedSeconds(routerCtx.clock, startedAtRef.current)
    const gradingStartedAt = _elapsedMs(routerCtx.clock, 0)
    // Clear any previous failure before a retry; success/failure is set below.
    if (gradeError !== null) setGradeError(null)

    try {
      await routerCtx.tel.span({ name: 'training.submit', target: 'job', input: { qid: item.flashcard.qid } }, async (spanTel) => {
        if (recording) {
          const transcript = await transcribeAnswer(routerCtx.rpcClient, recording, spanTel)
          if (submissionRef.current !== submission) return
          partStates[0] = { ...partStates[0], textValue: transcript }
          editorHandleRef.current?.setAnswer(promptBlock.id, { text: transcript, audio: recording })
        }
        const result = await gradeQuestion({ parts, states: partStates, excerpt: excerptBlocks, graders: _graders(routerCtx.rpcClient), tel: spanTel })
        const { partMarks, feedback: gradeFeedback } = result
        if (submissionRef.current !== submission) return
        await reviewMutation.mutateAsync({
          tel: spanTel,
          flashcardId: item.flashcard.qid,
          mark: result.score,
          ttaSeconds,
        })
        if (submissionRef.current !== submission) return
        startTransition(() => {
          setEntry({ resolution: 'marked', mark: result.score, ...(parts.length > 1 && { partMarks }) })
          setFeedback(gradeFeedback)
        })
        if (result.score === 1) _advanceAfterGrade(result.score)
      })
    } catch (error) {
      if (submissionRef.current !== submission) return
      // Surface — never swallow. Card stays unscored so the user can retry.
      submitInFlightRef.current = false
      if (singleChoice && editorHandleRef.current) {
        editorHandleRef.current.setAnswer(promptBlock.id, {})
      }
      startTransition(() => {
        setGradeError(error)
      })
    } finally {
      if (submissionRef.current === submission) {
        startedAtRef.current += _elapsedMs(routerCtx.clock, gradingStartedAt)
        setGrading(false)
        submitInFlightRef.current = false
      }
    }
  }

  async function handleAcceptDeletion() {
    if (!item) return
    if (gradeError !== null) setGradeError(null)

    try {
      await acceptDeletionMutation.mutateAsync({
        flashcardId: item.flashcard.qid,
      })

      navigate({ to: '/$workspaceId/cards', params: { workspaceId: loaderData.progressWorkspaceId } })
    } catch (error) {
      startTransition(() => {
        setGradeError(error)
      })
    }
  }

  function handleReveal() {
    if (cardBlocked || !canReveal || submitInFlightRef.current || reviewMutation.isPending) return
    setEntry({ resolution: 'revealed', mark: -1 })
  }

  function handleSpokenResponseRecorded(audio: UFile) {
    if (cardBlocked) return
    const editor = editorHandleRef.current
    if (!editor) return
    if (!promptBlock) return
    editor.setAnswer(promptBlock.id, { audio })
    setEntry({ resolution: 'revealed', mark: -1 })
  }

  async function handleManualScore(mark: number) {
    if (cardBlocked || !(item && promptBlock) || reviewMutation.isPending || submitInFlightRef.current) return
    if (gradeError !== null) setGradeError(null)

    try {
      await routerCtx.tel.span({ name: 'training.score', target: 'job', input: { qid: item.flashcard.qid, mark } }, tel => reviewMutation.mutateAsync({
        tel,
        flashcardId: item.flashcard.qid,
        mark,
        ttaSeconds: _elapsedSeconds(routerCtx.clock, startedAtRef.current),
      }))

      startTransition(() => {
        setEntry({ resolution: 'marked', mark })
        _advanceAfterGrade(mark)
      })
      // Grading IS the decision, and the learner made it — they already know
      // whether they were right. A "Marked correct." banner and a Next button
      // both restate what they just did, so grading advances the sitting and
      // says nothing. The mark is passed, never read from `score`: that state
      // update has not rendered yet in this tick.
    } catch (error) {
      startTransition(() => {
        setGradeError(error)
      })
    }
  }

  function handleNext() {
    if (score === null) return
    if (flashcard.deletedAt !== null) {
      navigateToSummary()
      return
    }
    if (!counters) {
      navigate({
        to: '/$workspaceId/training',
        params: { workspaceId: params.workspaceId },
      })
      return
    }
    _advanceAfterGrade(score)
  }

  /**
   * Takes the mark as an ARGUMENT rather than reading `score`.
   *
   * Grading advances the sitting, and the advance runs in the same tick as the
   * state update that records the mark — so `score` is still its pre-render
   * value there. Reading it made the advance silently no-op and left the
   * learner on a graded card with a Next button.
   */
  function _advanceAfterGrade(mark: number) {
    // A card opened directly has no sitting queue. Its saved review completes
    // a one-card session rather than asking an absent queue to advance.
    if (!counters) {
      navigate({
        to: '/$workspaceId/training/end',
        params: { workspaceId: params.workspaceId },
        search: { errors: String(mark < 1 ? 1 : 0), total: '1' },
        replace: true,
      })
      return
    }
    // One door: the counters and the move commit together. The advance pops the
    // head and pushes the next card, so back lands on this card with its own
    // entry state intact and the counters untouched.
    //
    // Whether this was the LAST card is not decided here. Behind the head the
    // departure pop does not fire and the advance resumes onto the head, so
    // "cards remaining" is not the same question as "is this the last card".
    // The drain below answers it.
    advanceQueue({
      queueState: (previous, { cards, current }) => cards[0]?.flashcardId === current.flashcardId ? ({
        answered: Math.min((previous?.answered ?? 0) + 1, sessionTotal),
        errors: (previous?.errors ?? 0) + (mark < 1 ? 1 : 0),
        total: previous?.total ?? sessionTotal,
      }) : previous,
    })
  }

  // The queue drained (§4.4 step 3: it stays put, counters still readable). The
  // finish screen is a route here, so go to it. `counters` gates the redirect:
  // a cold load on a card URL has no queue at all and must just render.
  function navigateToSummary() {
    navigate({
      to: '/$workspaceId/training/end',
      params: { workspaceId: params.workspaceId },
      search: {
        errors: String(counters?.errors ?? 0),
        total: String(sessionTotal),
      },
      replace: true,
    })
  }
  // An unanswerable card is passed over ungraded: it leaves the session total, so the
  // summary scores only graded cards, while answered and errors stay as they were.
  // Same head guard as grading: a skip behind the head (after Back) changes nothing.
  // A card opened directly has no queue to move; reopening training would serve the
  // same card, so it leaves the way Back does.
  function handleSkipInvalid() {
    if (validQuestion) return
    if (!counters) {
      handleBack()
      return
    }
    advanceQueue({
      queueState: (previous, { cards, current }) => cards[0]?.flashcardId === current.flashcardId ? ({
        answered: previous?.answered ?? 0,
        errors: previous?.errors ?? 0,
        total: Math.max((previous?.total ?? sessionTotal) - 1, previous?.answered ?? 0),
      }) : previous,
    })
  }
  function handleEditPage() {
    navigate({ to: '/$workspaceId/spaces/$spaceId/pages/$pageId', params: { workspaceId: loaderData.workspaceId, spaceId: page.spaceId, pageId: page.id }, search: { qid: flashcard.qid } })
  }
  const finishRun = useEffectEvent(navigateToSummary)
  const skipFrozenCard = useEffectEvent(() => { advanceQueue() })

  useEffect(() => {
    if (counters && size > 0 && flashcard.frozenAt !== null) skipFrozenCard()
  }, [counters, size, flashcard.frozenAt])

  useEffect(() => {
    if (!counters || size > 0 || flashcard.deletedAt !== null || pendingDeletion) return
    finishRun()
  }, [counters, size, flashcard.deletedAt, pendingDeletion])
  const reviewPending = reviewMutation.isPending || grading
  const visibleGradeError =
    gradeError === null ? null : t('gradingFailed', { error: unknownErrorToError(gradeError).message })

  const handleTrainingKeyDown = (event: ShortcutEvent) => {
    const submitsFromSingleLineAnswer =
      event.key === 'Enter' &&
      event.singleLineAnswer
    if (
      event.defaultPrevented ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      (event.textEntry && !submitsFromSingleLineAnswer)
    ) {
      return
    }

    if (
      score === null &&
      parts.length === 1 &&
      promptBlock?.type === 'question' &&
      (promptBlock.subtype === 'sc' || promptBlock.subtype === 'mc') &&
      /^[1-9]$/.test(event.key)
    ) {
      const index = Number(event.key) - 1
      if (index >= (promptBlock.opts?.length ?? 0)) return
      const editor = editorHandleRef.current
      if (!editor) return
      event.preventDefault()
      if (promptBlock.subtype === 'sc') {
        editor.setAnswer(promptBlock.id, { selectedIndex: index })
        return
      }
      const next = new Set(answer.selectedIndices ?? [])
      if (next.has(index)) next.delete(index)
      else next.add(index)
      editor.setAnswer(promptBlock.id, {
        selectedIndices: [...next].toSorted((left, right) => left - right),
      })
      return
    }

    if (event.key !== 'Enter') return
    if (event.button)
      return
    if (score !== null) {
      event.preventDefault()
      handleNext()
      return
    }
    if (reviewPending || revealed || singleChoice) return
    event.preventDefault()
    if (hasAnswer && !spokenResponse) void handleSubmit()
    else if (canReveal) handleReveal()
  }

  useKeyboardShortcut(Object.fromEntries(
    ['enter', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((key) => [key, handleTrainingKeyDown]),
  ))

  const submitSelection = useEffectEvent(() => {
    const value = answer
    if (singleChoice && value.selectedIndex !== undefined) {
      queueMicrotask(() => { void handleSubmit(answerViewerState(value)) })
    }
  })
  useEffect(() => {
    if (handle && answer.selectedIndex !== undefined) submitSelection()
  }, [handle, answer.selectedIndex])

  if (media.pending) return <LoadingSpinner />

  return (
    <AudioOptionsProvider value={routerCtx.testSupport?.audioOptions}>
    <ImageLoadingProvider pending={media.pending} onRetry={media.retry}>
      <main
        data-testid="flashcards-training-page"
        className="bg-surface-1 text-on-surface h-dvh max-h-full overflow-hidden"
      >
        <div className="mx-auto flex h-full min-h-0 max-w-2xl flex-col">
          {levelMutation.error || cloneMutation.error ? <p role="alert">{(levelMutation.error ?? cloneMutation.error)?.message}</p> : null}
                    {feedback ? (
                      <div
                        data-testid="flashcards-training-feedback"
                        className="border-separator bg-surface-2 text-on-surface rounded-lg border p-4 text-sm"
                      >
                        {feedback}
                      </div>
                    ) : null}
          {pendingDeletion ? <section className="flex flex-col gap-3 px-6 py-3">
            <p>Deleted by {actorName}.</p>
            <div className="flex gap-3">
              <Button data-testid="flashcards-training-accept-deletion" loading={acceptDeletionMutation.isPending} onClick={() => { void handleAcceptDeletion() }}>Accept</Button>
              <Button data-testid="flashcards-training-clone-deletion" loading={cloneMutation.isPending} onClick={() => { cloneMutation.mutate(null) }}>Clone</Button>
            </div>
          </section> : null}
          {/* The shell stays on an unanswerable card: the learner can still leave or skip it. */}
          {promptBlock || !validQuestion ? (
              <>
                {/* A lexicon entry page is TITLED with its headword, which is the
                answer to form recall, dictation and every cell card. Printing
                it in the center hands the answer over. */}
                <TrainingProgress
                  backLabel={t('backToPages')}
                  current={progressCurrent}
                  progressLabel={t('progress')}
                  title={
                    'kind' in item.page.content &&
                    item.page.content.kind === 'lexiconEntry'
                      ? ''
                      : item.page.title.trim() || 'Untitled page'
                  }
                  total={sessionTotal}
                  onBack={handleBack}
                />
              </>
          ) : null}
          {validQuestion ? promptBlock ? (
              <>
                <section
                  className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-6 scroll-py-10 sm:px-8"
                  style={{ marginBottom: keyboardOpen ? keyboardInset : 0 }}
                  data-slot="training-content-viewport"
                  data-testid={`flashcards-training-card-${item.flashcard.qid}`}
                >
                  <TrainingScrollContent keyboardOpen={keyboardOpen}>
                    <div aria-hidden="true" className="flex-1" />
                    <div className="flex shrink-0 flex-col gap-8">
                      <div className="w-full">
                        {lexiconTaskLabel ? (
                          <div className="mb-4 flex justify-center">
                            <Badge semanticRole="primary" size="sm">
                              {lexiconTaskLabel}
                            </Badge>
                          </div>
                        ) : null}
                        {deletedReview ? <DeletedReviewEditor doc={trainingDoc} spaceId={page.spaceId}
                          fileHandler={trainingFileHandler} className="outline-none" /> : <BlockEditor
                        spaceId={page.spaceId}
                          key={item.flashcard.qid}
                          doc={trainingDoc}
                          mode="answer"
                          answerActiveQid={item.question.qid}
                          answerAutoFocus={!controls?.reveal}
                          onClozeHintFail={() => {
                            void handleManualScore(0)
                          }}
                          onChange={_noop}
                          onReady={handleEditorReady}
                          fileHandler={trainingFileHandler}
                          className="outline-none"
                        />}
                        {spokenResponse && showAnswer && promptBlock ? <p aria-label="Answer" className="mt-4 text-center">{describeTrainingAnswer(promptBlock)}</p> : null}
                      </div>

                      {visibleGradeError ? (
                        <div
                          className="border-error bg-surface-error text-on-surface-error mx-auto w-full max-w-lg rounded-lg border px-4 py-3 text-sm"
                          data-testid="flashcards-training-error"
                        >
                          <ErrorDetails error={gradeError} title={visibleGradeError} compact />
                        </div>
                      ) : null}
                    </div>
                    <div aria-hidden="true" className="flex-1" />
                  </TrainingScrollContent>
                </section>

                <fieldset disabled={cardBlocked}
                  hidden={keyboardOpen || deletedReview}
                  className="fixed inset-x-0 bottom-0 z-10 px-2 pb-6 sm:px-8"
                  data-testid="flashcards-training-controls"
                >
                  <Toolbar
                    className="bg-surface-2 mx-auto w-full max-w-xl flex-wrap justify-between gap-x-0! gap-y-1! border-0"
                    variant="floating"
                  >
                    <Progress
                      aria-label={`Time remaining: ${remainingSeconds} seconds`}
                      className={timerTone}
                      data-testid="flashcards-training-timer"
                      max={timeoutSeconds}
                      size="lg"
                      value={remainingSeconds}
                      variant="circular"
                    >
                      <ProgressTrack>
                        <ProgressIndicator />
                      </ProgressTrack>
                      <span className="absolute text-xs tabular-nums select-none">
                        {remainingSeconds}
                      </span>
                    </Progress>
                    <ToolbarGroup>
                      <TrainingLevelMenu
                        label="Importance"
                        value={levels.importance}
                        onValueChange={(value) => {
                          handleLevelChange('importance', value)
                        }}
                      />
                    </ToolbarGroup>
                    {singleChoice && score === null && !revealed ? null : (
                      <ToolbarGroup data-testid="flashcards-training-action-group"
                        className="[&&_.training-action-left]:rounded-l-[26px]! [&&_.training-action-left]:rounded-r-sm! [&&_.training-action-right]:rounded-l-sm! [&&_.training-action-right]:rounded-r-[26px]!">
                      {score === null ? (
                        revealed ? (
                          <>
                            <ToolbarButton
                              data-testid="flashcards-training-manual-wrong"
                              className="training-action-left size-13"
                              render={(props) => (
                                <IconButton
                                  {...props}
                                  disabled={reviewMutation.isPending}
                                  semanticRole="error"
                                  size="lg"
                                  variant="filled"
                                  onClick={() => {
                                    void handleManualScore(0)
                                  }}
                                >
                                  <XIcon />
                                  {t('wrong')}
                                </IconButton>
                              )}
                            />
                            <ToolbarButton
                              data-testid="flashcards-training-manual-right"
                              className="training-action-right size-13"
                              render={(props) => (
                                <IconButton
                                  {...props}
                                  disabled={reviewMutation.isPending}
                                  semanticRole="success"
                                  size="lg"
                                  variant="filled"
                                  onClick={() => {
                                    void handleManualScore(1)
                                  }}
                                >
                                  <CheckIcon />
                                  {t('right')}
                                </IconButton>
                              )}
                            />
                          </>
                        ) : spokenResponse ? (
                          <AudioRecorder
                            buttonClassName="size-13"
                            className="size-13"
                            size="lg"
                            variant="filled"
                            onRecorded={handleSpokenResponseRecorded}
                          />
                        ) : controls ? (
                          <>
                            {(controls.primary === 'voice' ? ['voice', 'reveal'] : ['reveal', 'voice']).map(action => {
                              const primary = action === controls.primary
                              const buttonClassName = `size-13 ${controls.voice && controls.reveal ? primary ? 'training-action-left' : 'training-action-right' : ''}`
                              if (action === 'voice') return controls.voice ? (
                                <fieldset key={action} disabled={reviewPending} className="border-0 p-0"
                                  data-testid="flashcards-training-voice" data-primary={primary}>
                                  <AudioRecorder
                                    key={item.flashcard.qid}
                                    buttonClassName={`${buttonClassName} bg-primary text-on-primary`}
                                    size="lg"
                                    variant="filled"
                                    onRecorded={(audio) => { void handleSubmit(answerViewerState(answer), audio) }}
                                  />
                                </fieldset>
                              ) : null
                              return controls.reveal ? (
                                <ToolbarButton key={action}
                                  data-testid="flashcards-training-show-answer" data-primary={primary}
                                  className={buttonClassName}
                                  render={(props) => (
                                    <IconButton {...props} disabled={reviewPending}
                                      size="lg" variant="filled"
                                      onClick={handleReveal}>
                                      <EyeIcon />{t('showAnswer')}
                                    </IconButton>
                                  )}
                                />
                              ) : null
                            })}
                            {hasAnswer || grading ? (
                              <IconButton data-testid="flashcards-training-submit" disabled={reviewPending}
                                loading={grading} onClick={() => { void handleSubmit() }}>
                                <SendIcon />{t('submit')}
                              </IconButton>
                            ) : null}
                          </>
                        ) : (
                          <ToolbarButton
                            data-testid="flashcards-training-submit"
                            className="size-13"
                            render={(props) => (
                              <IconButton
                                {...props}
                                disabled={
                                  reviewMutation.isPending || !hasAnswer
                                }
                                loading={reviewPending}
                                size="lg"
                                variant="filled"
                                onClick={() => {
                                  void handleSubmit()
                                }}
                              >
                                <SendIcon />
                                {t('submit')}
                              </IconButton>
                            )}
                          />
                        )
                      ) : (
                        <ToolbarButton
                          data-testid="flashcards-training-next"
                          className="size-13"
                          render={(props) => (
                            <IconButton
                              {...props}
                              size="lg"
                              variant="filled"
                              onClick={handleNext}
                            >
                              <ChevronRightIcon />
                              {t('next')}
                            </IconButton>
                          )}
                        />
                      )}
                      </ToolbarGroup>
                    )}
                    <ToolbarGroup>
                      <TrainingLevelMenu
                        label="Difficulty"
                        value={levels.difficulty}
                        onValueChange={(value) => {
                          handleLevelChange('difficulty', value)
                        }}
                      />
                    </ToolbarGroup>
                  </Toolbar>
                </fieldset>
              </>
          ) : (
            <TrainingEmptyState onBack={handleBack} />
          ) : (
            <section role="alert" className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 pt-4 pb-28 sm:px-8">
              <BlockEditor
                spaceId={page.spaceId}
                doc={trainingDoc}
                mode="read"
                onChange={_noop}
                fileHandler={trainingFileHandler}
                className="outline-none"
              />
              {incompatiblePrompt ? <>
                <p>Your actual prompt type is incompatible with the source question.</p>
                <Button disabled={promptRepair.isPending} onClick={() => { promptRepair.mutate(null) }}>Use authored {authoredPromptType}</Button>
                {promptRepair.error ? <p role="alert">{promptRepair.error.message}</p> : null}
              </> : <>
                <p>Fix the question</p>
                {parts.length === 0 ? <p>This card has no answerable question.</p> : null}
              </>}
              <div className="fixed inset-x-0 bottom-0 z-10 px-2 pb-6 sm:px-8" data-testid="flashcards-training-controls">
                <Toolbar className="bg-surface-2 mx-auto w-full max-w-xl border-0" variant="floating">
                  <ToolbarGroup
                    className="flex w-full [&&_.training-action-left]:rounded-l-[26px]! [&&_.training-action-left]:rounded-r-sm! [&&_.training-action-right]:rounded-l-sm! [&&_.training-action-right]:rounded-r-[26px]!">
                    <ToolbarButton
                      data-testid="flashcards-training-edit-page"
                      className="training-action-left h-13 w-auto min-w-0 flex-1 px-5"
                      render={(props) => (
                        <Button {...props} variant="outlined" onClick={handleEditPage}>
                          <PencilIcon />Edit page
                        </Button>
                      )}
                    />
                    <ToolbarButton
                      data-testid="flashcards-training-skip"
                      className="training-action-right h-13 w-auto min-w-0 flex-1 px-5"
                      render={(props) => (
                        <Button {...props} variant="filled" onClick={handleSkipInvalid}>
                          <SkipForwardIcon />Skip
                        </Button>
                      )}
                    />
                  </ToolbarGroup>
                </Toolbar>
              </div>
            </section>
          )}
        </div>
      </main>
    </ImageLoadingProvider>
    </AudioOptionsProvider>
  )
}

function TrainingProgress({
  backLabel,
  current,
  progressLabel,
  title,
  total,
  onBack,
}: {
  backLabel: string
  current: number
  progressLabel: string
  title: string
  total: number
  onBack: () => void
}) {
  return (
    <div className="flex shrink-0 flex-col gap-3 pt-4">
      <div className="text-on-surface-secondary flex items-center gap-3 pr-6 pl-3 text-sm">
        <IconButton
          data-testid="flashcards-training-back"
          semanticRole="secondary"
          variant="text"
          onClick={onBack}
        >
          <ChevronLeftIcon />
          {backLabel}
        </IconButton>
        <span className="min-w-0 truncate text-center select-none">
          {title}
        </span>
        <span className="flex-1 text-right tabular-nums select-none">
          {current}/{total}
        </span>
      </div>
      <Progress
        aria-label={`${progressLabel} ${current} of ${total}`}
        className="w-full"
        data-testid="flashcards-training-progress"
        max={total}
        size="sm"
        value={current}
      />
    </div>
  )
}

function TrainingLevelMenu({
  label,
  value,
  onValueChange,
}: {
  label: 'Importance' | 'Difficulty'
  value: TrainingLevel
  onValueChange: (value: string | null) => void
}) {
  const LevelIcon =
    label === 'Importance' ? ImportanceLevelIcon : DifficultyLevelIcon

  return (
    <Menu>
      <MenuTrigger
        render={
          <IconButton
            className="size-[52px]"
            data-testid={`flashcards-training-${label.toLowerCase()}`}
            size="lg"
          />
        }
      >
        <LevelIcon level={value} />
        {label}
      </MenuTrigger>
      <MenuPopup>
        <MenuGroup>
          <MenuGroupLabel>{label}</MenuGroupLabel>
          <MenuRadioGroup value={value} onValueChange={onValueChange}>
            <MenuRadioItem value="low">
              <LevelIcon level="low" />
              Low
            </MenuRadioItem>
            <MenuRadioItem value="medium">
              <LevelIcon level="medium" />
              Medium
            </MenuRadioItem>
            <MenuRadioItem value="high">
              <LevelIcon level="high" />
              High
            </MenuRadioItem>
          </MenuRadioGroup>
        </MenuGroup>
      </MenuPopup>
    </Menu>
  )
}

function _elapsedSeconds(clock: Clock, startedAt: number) {
  return Math.max(Math.round(_elapsedMs(clock, startedAt) / 1000), 1)
}

function _elapsedMs(clock: Clock, startedAt: number) {
  return clock.now() - startedAt
}

function _sessionCount(raw: string | undefined, fallback: number) {
  const parsed = Number(raw)
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : fallback
}

function _getPageBlocks(children: unknown): Block[] {
  return Array.isArray(children) ? (children as Block[]) : []
}

function _noop() {}

function _createTrainingBlocksCache() {
  const blocksByCard = new Map<string, { source: string; shuffled: Block[] }>()
  return {
    get(input: {
      blocks: Block[]
      flashcardId: string
      random?: () => number
    }) {
      if (trainingChoices(input.blocks).length === 0) return input.blocks
      const source = JSON.stringify(input.blocks)
      const cached = blocksByCard.get(input.flashcardId)
      if (cached?.source === source) return cached.shuffled
      const shuffled = shuffleTrainingChoiceOptions(input.blocks, input.random)
      blocksByCard.set(input.flashcardId, { source, shuffled })
      return shuffled
    },
  }
}

const _noopFileHandler = {
  getUrl: (src: string) => src,
  upload: () =>
    Promise.resolve({
      items: [],
      done: Promise.resolve({ srcs: [] }),
    }),
}

/**
 * One document per distinct card view. Rebuilt only when the rendered blocks
 * change, so the editor re-feeds the card on reveal and keeps it otherwise.
 */
function _createTrainingDocCache() {
  let current: { key: string; doc: Doc } | undefined
  return {
    get(blocks: Blocks) {
      const key = JSON.stringify(blocks)
      if (current?.key !== key) {
        current = { key, doc: new Doc({ content: { children: structuredClone(blocks) as never } }) }
      }
      return current.doc
    },
  }
}

function _hasPartAnswer(answer: QuestionAnswerValue) {
  return (answer.text?.trim().length ?? 0) > 0 ||
    answer.audio !== undefined ||
    answer.selectedIndex !== undefined ||
    (answer.selectedIndices?.length ?? 0) > 0
}

function _lexiconTaskLabel(
  entry: LexiconEntry,
  qid: string,
): 'SAY' | 'SPELL' | 'MEANING' | undefined {
  const kind = getLexiconCardType(entry, qid)
  if (kind === 'readAloud') return 'SAY'
  if (kind === 'dictation') return 'SPELL'
  if (kind === 'senseRecall') return 'MEANING'
  return undefined
}

function _withoutLexiconTaskLabel(blocks: Blocks, qid: string, label: string): Blocks {
  const result: Blocks = []
  for (const block of blocks) {
    if (
      block.type === 'question' &&
      block.qid === qid &&
      Array.isArray(block.question)
    ) {
      result.push({
        ...block,
        question: block.question.filter(
          (questionBlock, index) => {
            // CRDT persistence replaces generated IDs. Only the leading generated
            // task cue belongs to the badge; authored prompt content stays intact.
            if (index !== 0 || questionBlock.type !== 'p' || questionBlock.children.length !== 1) return true
            const emphasis = questionBlock.children[0]
            return !(emphasis.type === 'em' && emphasis.color === 'secondary' &&
              emphasis.children.length === 1 && emphasis.children[0].type === 'text' && emphasis.children[0].text === label)
          },
        ),
      } as Block)
    } else if ('children' in block && Array.isArray(block.children)) {
      result.push({ ...block, children: _withoutLexiconTaskLabel(block.children as Blocks, qid, label) } as Block)
    } else result.push(block)
  }
  return result
}


function TrainingScrollContent({ children, keyboardOpen }: {
  children: ReactNode
  keyboardOpen: boolean
}) {
  const scroll = useScrollContainer()
  const { headerRef } = scroll
  useViewportInset(() => { scroll.revealFocused() })
  return <section ref={headerRef}
    onFocusCapture={() => { scroll.revealFocused() }}
    className={`mx-auto flex min-h-full w-full max-w-xl flex-col ${keyboardOpen ? 'py-4' : 'pt-10 pb-28'}`}>
    {children}
  </section>
}

/** Option units use authored positions; the card keeps the shuffled positions of every choice on it. */
function _choiceTranslationRows(rows: TranslationRow[], source: Blocks, shuffled: Blocks): TranslationRow[] {
  const displayedById = new Map(trainingChoices(shuffled).map(choice => [choice.id, choice]))
  return rows.map(row => {
    for (const original of trainingChoices(source)) {
      const index = original.opts.findIndex((_option, index) => row.blockId === `${original.id}:option:${index}`)
      if (index < 0) continue
      const displayed = displayedById.get(original.id)
      const position = displayed ? displayed.opts.findIndex(option => option[0]?.id === original.opts[index][0]?.id) : -1
      return position < 0 ? row : { ...row, blockId: `${original.id}:option:${position}` }
    }
    return row
  })
}
